
export interface ExportableEvent {
  startTime: string;
  jobCode: string;
  jobUrl?: string;
  type: string;
  status: string;
  customerName: string;
  revenue: number;
  currency: string;
  totalLaborCost: number;
  laborPercentage: string | number;
  trucks: string;
  crew: string;
  truckCount: number;
  crewCount: number;
  // Optional fields for enhanced export (dispatch component)
  customerPhone?: string;
  arrivalWindow?: string;
  duration?: string;
  travelTime?: string;
  mileage?: string;
  locations?: string;
  weight?: string;
  volume?: string;
  crewRequirement?: number;
}

export function generateScheduleHtml(events: ExportableEvent[]): string {
  // Detect which optional fields have data
  const hasCustomerPhone = events.some(e => e.customerPhone);
  const hasArrivalWindow = events.some(e => e.arrivalWindow);
  const hasDuration = events.some(e => e.duration);
  const hasTravelTime = events.some(e => e.travelTime);
  const hasMileage = events.some(e => e.mileage);
  const hasLocations = events.some(e => e.locations);
  const hasWeight = events.some(e => e.weight);
  const hasVolume = events.some(e => e.volume);
  const hasCrewRequirement = events.some(e => e.crewRequirement != null);

  let html = '<table border="1" style="border-collapse: collapse;">';
  html += '<thead><tr><th>Start Time</th><th>Job Code</th><th>Type</th><th>Status</th><th>Customer</th>';
  if (hasCustomerPhone) html += '<th>Customer Phone</th>';
  if (hasArrivalWindow) html += '<th>Arrival Window</th>';
  if (hasDuration) html += '<th>Duration</th>';
  if (hasTravelTime) html += '<th>Travel Time</th>';
  if (hasMileage) html += '<th>Mileage</th>';
  if (hasLocations) html += '<th>Locations</th>';
  if (hasWeight) html += '<th>Weight</th>';
  if (hasVolume) html += '<th>Volume</th>';
  html += '<th>Revenue</th><th>Cost</th><th>Labor %</th><th>Truck</th><th>Crew</th><th>Truck Count</th><th>Crew Count</th>';
  if (hasCrewRequirement) html += '<th>Crew Requirement</th>';
  html += '</tr></thead>';
  html += '<tbody>';

  events.forEach(event => {
    const revenue = formatCurrencyValue(event.revenue, event.currency);
    const cost = formatCurrencyValue(event.totalLaborCost, event.currency);
    const laborPct = typeof event.laborPercentage === 'number' ? event.laborPercentage.toFixed(1) + '%' : event.laborPercentage;
    
    const jobLink = event.jobUrl && event.jobCode ? `<a href="${event.jobUrl}">${event.jobCode}</a>` : (event.jobCode || '');

    html += '<tr>';
    html += `<td>${event.startTime}</td>`;
    html += `<td>${jobLink}</td>`;
    html += `<td>${event.type}</td>`;
    html += `<td>${event.status}</td>`;
    html += `<td>${event.customerName}</td>`;
    if (hasCustomerPhone) html += `<td>${event.customerPhone || ''}</td>`;
    if (hasArrivalWindow) html += `<td>${event.arrivalWindow || ''}</td>`;
    if (hasDuration) html += `<td>${event.duration || ''}</td>`;
    if (hasTravelTime) html += `<td>${event.travelTime || ''}</td>`;
    if (hasMileage) html += `<td>${event.mileage || ''}</td>`;
    if (hasLocations) html += `<td>${event.locations || ''}</td>`;
    if (hasWeight) html += `<td>${event.weight || ''}</td>`;
    if (hasVolume) html += `<td>${event.volume || ''}</td>`;
    html += `<td>${revenue}</td>`;
    html += `<td>${cost}</td>`;
    html += `<td>${laborPct}</td>`;
    html += `<td>${event.trucks}</td>`;
    html += `<td>${event.crew}</td>`;
    html += `<td>${event.truckCount}</td>`;
    html += `<td>${event.crewCount}</td>`;
    if (hasCrewRequirement) html += `<td>${event.crewRequirement ?? ''}</td>`;
    html += '</tr>';
  });

  html += '</tbody></table>';
  return html;
}

function formatCurrencyValue(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(amount / 100);
}
